import { ListChecks, MessageSquare, Play, TrendingUp, Wallet } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Button, Card, CardHeader, EmptyState, PageSection, StatusBadge, Textarea } from '@/ui/components';
import { pmAgentService, type PmAgentGridColumn, type PmAgentRunResult } from '@/services/pmAgentService';
import './pmAgentPanel.css';
import PmProjectUpdates from './PmProjectUpdates';

function normalizeErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'PM Agent query failed.';
}

function formatCellValue(value: unknown, type?: string): string {
  if (value === null || value === undefined || value === '') return '-';
  if (type === 'currency') {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric.toLocaleString('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
      });
    }
  }
  if (type === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatJson(value: unknown): string {
  return JSON.stringify(value || {}, null, 2);
}

function hasDisplayValue(value: unknown): boolean {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function getPreferredColumn(
  row: Record<string, unknown>,
  columns: PmAgentGridColumn[],
  keys: string[],
): PmAgentGridColumn | null {
  const keySet = new Set(keys.map((key) => key.toLowerCase()));
  return columns.find((column) => keySet.has(column.key.toLowerCase()) && hasDisplayValue(row[column.key])) || null;
}

function getMobileRowTitle(row: Record<string, unknown>, columns: PmAgentGridColumn[]): string {
  const primaryColumn = getPreferredColumn(row, columns, [
    'title',
    'invoiceNumber',
    'quotationNumber',
    'projectNumber',
    'taskNumber',
    'clientName',
    'name',
    'company',
    'projectAddress',
    'address',
  ]) || columns.find((column) => hasDisplayValue(row[column.key]));

  return primaryColumn ? formatCellValue(row[primaryColumn.key], primaryColumn.type) : 'PMapp row';
}

function getMobileRowSubtitle(row: Record<string, unknown>, columns: PmAgentGridColumn[]): string {
  const subtitleParts = [
    getPreferredColumn(row, columns, ['clientName', 'client', 'company']),
    getPreferredColumn(row, columns, ['projectAddress', 'address']),
    getPreferredColumn(row, columns, ['status', 'paymentStatus']),
  ].map((column) => column ? formatCellValue(row[column.key], column.type) : '')
    .filter((value, index, values) => value && values.indexOf(value) === index);

  return subtitleParts.slice(0, 3).join(' · ');
}

function getMobileRowFields(row: Record<string, unknown>, columns: PmAgentGridColumn[]): PmAgentGridColumn[] {
  const preferredKeys = [
    'amount',
    'totalReceivable',
    'paidAmount',
    'status',
    'paymentStatus',
    'dueDate',
    'paidDate',
    'invoiceDate',
    'createdDate',
    'projectNumber',
    'quotationNumber',
    'suburb',
  ];
  const preferred = preferredKeys
    .map((key) => getPreferredColumn(row, columns, [key]))
    .filter((column): column is PmAgentGridColumn => Boolean(column));
  const fallback = columns.filter((column) => hasDisplayValue(row[column.key]));
  const merged = [...preferred, ...fallback].filter((column, index, list) => (
    list.findIndex((candidate) => candidate.key === column.key) === index
  ));

  return merged.slice(0, 6);
}

export default function PmAgentPanel() {
  const [view, setView] = useState<'updates' | 'query'>('updates');
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<PmAgentRunResult | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackTone, setFeedbackTone] = useState<'success' | 'error'>('success');
  const [loading, setLoading] = useState(false);

  const visibleRows = useMemo(() => result?.result.rows.slice(0, 25) || [], [result]);
  const columns = useMemo(() => {
    if (result?.result.columns?.length) return result.result.columns;
    const first = visibleRows[0] || {};
    return Object.keys(first).map((key) => ({
      key,
      label: key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (char) => char.toUpperCase()),
      type: 'string' as const,
    }));
  }, [result, visibleRows]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const message = query.trim();
    if (!message) {
      setFeedback('Enter a question before running PM Agent.');
      setFeedbackTone('error');
      return;
    }

    setLoading(true);
    setFeedback(null);
    try {
      const nextResult = await pmAgentService.query({ message });
      setResult(nextResult);
      setFeedback(`PM Agent ran ${nextResult.selectedTool} and returned ${nextResult.result.rowCount} row${nextResult.result.rowCount === 1 ? '' : 's'}.`);
      setFeedbackTone('success');
    } catch (error) {
      setFeedback(normalizeErrorMessage(error));
      setFeedbackTone('error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageSection width="full" padding="default">
      <div className="pm-agent-actions pm-agent-view-switch" aria-label="PM Agent views">
        <h2>{view === 'updates' ? 'Projects' : 'Ask PM Agent'}</h2>
        <div className="pm-agent-mode-group" role="group" aria-label="Agent mode">
        <Button variant={view === 'updates' ? 'primary' : 'secondary'} aria-label="Project updates" title="Project updates" className="pm-icon-button" aria-pressed={view === 'updates'} onClick={() => setView('updates')}><ListChecks size={20} aria-hidden="true" /></Button>
        <Button variant={view === 'query' ? 'primary' : 'secondary'} aria-label="Ask PM Agent" title="Ask PM Agent" className="pm-icon-button" aria-pressed={view === 'query'} onClick={() => setView('query')}><MessageSquare size={20} aria-hidden="true" /></Button>
        </div>
      </div>
      {view === 'updates' ? <PmProjectUpdates /> : <div className="pm-agent-layout">
        <div className="pm-agent-main">
          <Card
            header={(
              <CardHeader
                title="PM Agent"
                subtitle="Search PMapp records."
                actions={<StatusBadge status={result ? result.selectedTool : 'ready'}>{result ? result.selectedTool : 'Ready'}</StatusBadge>}
              />
            )}
          >
            <div className="pm-agent-stack">
              <form onSubmit={handleSubmit} className="pm-agent-composer">
                <Textarea
                  label="Question"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Show unpaid invoices in Monash"
                  rows={3}
                  density="compact"
                  fullWidth
                />
                <div className="pm-agent-actions">
                  <Button type="submit" loading={loading} aria-label="Run PM Agent" title="Run PM Agent" className="pm-icon-button"><Play size={18} /></Button>
                  <Button type="button" variant="secondary" className="pm-icon-button" aria-label="Revenue" title="Revenue" disabled={loading} onClick={() => setQuery('What is this month’s invoice revenue?')}>
                    <TrendingUp size={18} aria-hidden="true" />
                  </Button>
                  <Button type="button" variant="secondary" className="pm-icon-button" aria-label="Cashflow" title="Cashflow" disabled={loading} onClick={() => setQuery('Why is cashflow weak this month?')}>
                    <Wallet size={18} aria-hidden="true" />
                  </Button>
                </div>
                {feedback ? (
                  <div className={`company-ai-feedback company-ai-feedback--${feedbackTone}`}>
                    {feedback}
                  </div>
                ) : null}
              </form>

              {result ? (
                <div className="pm-agent-stack">
                  <div className="company-ai-result-box">
                    <div className="company-ai-section-title" style={{ marginTop: 0 }}>Analysis Summary</div>
                    <div className="company-ai-message-body">{result.summary || result.answer}</div>
                  </div>

                  <div className="pm-agent-result-shell">
                    <div className="pm-agent-result-header">
                      <div>
                        <div className="company-ai-section-title" style={{ marginTop: 0 }}>Structured Results</div>
                        <div className="company-ai-muted">{result.result.resultType} · {result.result.rowCount} row{result.result.rowCount === 1 ? '' : 's'}</div>
                      </div>
                    </div>
                    {visibleRows.length && columns.length ? (
                      <>
                        <div className="pm-agent-table-wrap">
                          <table className="pm-agent-table">
                            <thead>
                              <tr>
                                {columns.map((column) => (
                                  <th key={column.key}>{column.label}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {visibleRows.map((row, rowIndex) => (
                                <tr key={`pm-agent-row-${rowIndex}`}>
                                  {columns.map((column) => (
                                    <td key={`${rowIndex}-${column.key}`}>{formatCellValue(row[column.key], column.type)}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="pm-agent-mobile-results" aria-label="Structured result rows">
                          {visibleRows.map((row, rowIndex) => {
                            const subtitle = getMobileRowSubtitle(row, columns);
                            const fields = getMobileRowFields(row, columns);

                            return (
                              <article className="pm-agent-row-card" key={`pm-agent-mobile-row-${rowIndex}`}>
                                <div className="pm-agent-row-card__header">
                                  <strong>{getMobileRowTitle(row, columns)}</strong>
                                  <span>#{rowIndex + 1}</span>
                                </div>
                                {subtitle ? (
                                  <div className="pm-agent-row-card__subtitle">{subtitle}</div>
                                ) : null}
                                <dl className="pm-agent-row-card__fields">
                                  {fields.map((column) => (
                                    <div key={`${rowIndex}-${column.key}-mobile`} className="pm-agent-row-card__field">
                                      <dt>{column.label}</dt>
                                      <dd>{formatCellValue(row[column.key], column.type)}</dd>
                                    </div>
                                  ))}
                                </dl>
                              </article>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <EmptyState
                        icon="PM"
                        title="No rows returned"
                        description="The selected read-only tool completed without matching records."
                      />
                    )}
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon="PM"
                  title="Ask a PM Agent question"
                  description="Try unpaid invoices, projects in a council area, monthly revenue, or cashflow analysis."
                />
              )}
            </div>
          </Card>
        </div>

        <div className="pm-agent-side">
          <Card
            header={(
              <CardHeader
                title="Debug"
                subtitle="Intent, tool, and arguments only."
                actions={<StatusBadge status={result ? 'active' : 'idle'}>{result ? 'Active' : 'Idle'}</StatusBadge>}
              />
            )}
          >
            {result ? (
              <details className="pm-agent-debug">
                <summary>Show routing details</summary>
                <div className="pm-agent-debug-block">
                  <div className="company-ai-result-label">Intent</div>
                  <pre>{formatJson(result.debug.intent)}</pre>
                </div>
                <div className="pm-agent-debug-block">
                  <div className="company-ai-result-label">Selected Tool</div>
                  <pre>{result.debug.selectedTool}</pre>
                </div>
                <div className="pm-agent-debug-block">
                  <div className="company-ai-result-label">Arguments</div>
                  <pre>{formatJson(result.debug.arguments)}</pre>
                </div>
              </details>
            ) : (
              <EmptyState
                icon="{}"
                title="No debug data"
                description="Run a query to inspect the intent parser output and selected registered tool."
              />
            )}
          </Card>
        </div>
      </div>}
    </PageSection>
  );
}
